/**
 * Adapts the server's team workload document (`POST /analytics/workload`,
 * `analytics.workload`) into table rows. Pure — every count and every id
 * comes from the server; this only arranges them (T-56, T-58, T-73, T-74).
 */
import type {
  AnalyticsPolicy,
  PersonAging,
  StageSplit,
  WorkloadBucket,
  WorkloadResponse,
} from '@/services/dashboardAnalytics'

export const UNASSIGNED_LABEL = 'Unassigned'

/** Stages that are not "open work": the funded book and the dead. */
const CLOSED_STAGES = new Set(['Funded', 'Dead'])

export interface WorkloadRow {
  name: string
  unassigned: boolean
  onRoster: boolean
  open: { count: number; volume: number | null; ids: string[] }
  total: { count: number; volume: number | null; ids: string[] }
  byStage: Map<string, StageSplit>
  funded: StageSplit | null
  supporting: { count: number; ids: string[] }
  staffed: WorkloadBucket | null
  needsStaffing: WorkloadBucket | null
  aging: PersonAging | null
}

export interface StageColumnDef {
  stage: string
  label: string
}

function openIds(splits: StageSplit[]): string[] {
  return splits.filter((split) => !CLOSED_STAGES.has(split.stage)).flatMap((split) => split.dealIds)
}

function openVolume(splits: StageSplit[]): number | null {
  let total: number | null = null
  for (const split of splits) {
    if (CLOSED_STAGES.has(split.stage) || split.commitment == null) continue
    total = (total ?? 0) + split.commitment
  }
  return total
}

/** The open stages any row has a deal in, in the server's stage order. */
export function openStageColumns(response: WorkloadResponse): StageColumnDef[] {
  const used = new Set<string>()
  const scan = (splits: StageSplit[] | undefined) => {
    for (const split of splits ?? []) if (split.count > 0) used.add(split.stage)
  }
  for (const person of response.people) scan(person.byStage)
  scan(response.unassigned?.byStage)
  return (response.stages ?? [])
    .filter((stage) => !CLOSED_STAGES.has(stage.stage) && used.has(stage.stage))
    .map((stage) => ({ stage: stage.stage, label: stage.label ?? stage.stage }))
}

export function workloadRows(response: WorkloadResponse): WorkloadRow[] {
  const staffingByName = new Map<string, { staffed: WorkloadBucket; needsStaffing: WorkloadBucket }>()
  for (const entry of response.staffing?.byResponsibleParty ?? []) {
    staffingByName.set(entry.key == null ? UNASSIGNED_LABEL : entry.key, entry)
  }
  const ownsStaffing = response.role === 'responsibleParty'

  const rows: WorkloadRow[] = response.people.map((person) => {
    const stages = new Map(person.byStage.map((split) => [split.stage, split]))
    const staffing = ownsStaffing ? staffingByName.get(person.name) : undefined
    const open = openIds(person.byStage)
    return {
      name: person.name,
      unassigned: false,
      onRoster: Boolean(person.onRoster),
      open: { count: person.total.openCount ?? open.length, volume: person.total.openVolume ?? openVolume(person.byStage), ids: open },
      total: { count: person.total.count, volume: person.total.volume, ids: person.total.dealIds },
      byStage: stages,
      funded: stages.get('Funded') ?? null,
      supporting: { count: person.supporting?.count ?? 0, ids: person.supporting?.dealIds ?? [] },
      staffed: staffing?.staffed ?? (ownsStaffing ? { count: 0, volume: null, dealIds: [] } : null),
      needsStaffing: staffing?.needsStaffing ?? (ownsStaffing ? { count: 0, volume: null, dealIds: [] } : null),
      aging: person.aging ?? null,
    }
  })

  const orphan = response.unassigned
  if (orphan && orphan.count > 0) {
    const stages = new Map(orphan.byStage.map((split) => [split.stage, split]))
    const open = openIds(orphan.byStage)
    const staffing = ownsStaffing ? staffingByName.get(UNASSIGNED_LABEL) : undefined
    rows.push({
      name: UNASSIGNED_LABEL,
      unassigned: true,
      onRoster: false,
      open: { count: open.length, volume: openVolume(orphan.byStage), ids: open },
      total: { count: orphan.count, volume: null, ids: orphan.dealIds },
      byStage: stages,
      funded: stages.get('Funded') ?? null,
      supporting: { count: 0, ids: [] },
      staffed: staffing?.staffed ?? null,
      needsStaffing: staffing?.needsStaffing ?? null,
      aging: orphan.aging ?? null,
    })
  }
  return rows
}

export type WorkloadSort = 'open' | 'supporting' | 'needsStaffing' | 'triageAged' | 'quotesAged' | 'quoteSla' | 'funded'

export function sortValue(row: WorkloadRow, key: WorkloadSort): number {
  switch (key) {
    case 'open':
      return row.open.count
    case 'supporting':
      return row.supporting.count
    case 'needsStaffing':
      return row.needsStaffing?.count ?? -1
    case 'triageAged':
      return row.aging?.triageAgedCount ?? -1
    case 'quotesAged':
      return row.aging?.quotesOutAgedCount ?? -1
    case 'quoteSla':
      return row.aging?.quoteSlaBreachCount ?? -1
    case 'funded':
      return row.funded?.count ?? 0
  }
}

/** Unassigned last; otherwise the chosen column, then open work, then name. */
export function sortWorkload(rows: readonly WorkloadRow[], key: WorkloadSort): WorkloadRow[] {
  return [...rows].sort((a, b) => {
    if (a.unassigned !== b.unassigned) return a.unassigned ? 1 : -1
    return (
      sortValue(b, key) - sortValue(a, key) ||
      b.open.count - a.open.count ||
      b.total.count - a.total.count ||
      a.name.localeCompare(b.name)
    )
  })
}

/** "Triage 14d+ · quotes 14d+ · quote 3d" from the policy the server echoed. */
export function policyThresholds(policy: AnalyticsPolicy | undefined) {
  return {
    triage: policy?.triageAgedDays ?? null,
    quote: policy?.quoteAgedDays ?? null,
    quoteSla: policy?.quoteSlaDays ?? null,
    responseHours: policy?.responseSlaHours ?? null,
    ratified: !(policy?.status ?? '').toLowerCase().includes('unratified'),
  }
}

const SHORT: Record<string, string> = {
  Triage: 'Triage',
  'Pre-screen Indication': 'Pre-scr.',
  'Post-screen Quote': 'Quote',
  'Term Sheet Issued': 'TS iss.',
  'Term Sheet Signed': 'TS sig.',
  'IC / Committee Approved': 'IC',
  'Rate Lock': 'Lock',
  Funded: 'Funded',
  Dead: 'Dead',
}

export function stageShort(label: string): string {
  return SHORT[label] ?? label
}
